import { format } from 'date-fns';
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { useAuth } from '@gbs-monorepo-packages/auth';
import {
  BreadcrumbsComponent,
  DEBOUNCE_TIME,
  ETypeFolder,
  HighUserRoles,
  IColumn,
  IDocumentDTO,
  type IFolderDTO,
  INIT_PAGE,
  IPaginationMetaProps,
  ISort,
  LIMIT_PAGE,
  ListLoading,
  Logger,
  SearchBar,
  TableCell,
  TableImageCell,
  TableRow,
  formatUnknownErrorMessage,
  getRouteFrom,
  useBreadcrumbs,
  useToast,
} from '@gbs-monorepo-packages/common';

import { CLIENT_URL } from '../../../../../../../../constants/Env';
import {
  CREATE_TEMPLATE_MODAL_BUTTON_LINK_FOLDER_ITEMS,
  EDIT_COURSE_MODAL_BUTTON_LINK_FOLDER_ITEMS,
} from '../../../../../../../../constants/RoutePaths';
import { VALIDATION_LIMITS } from '../../../../../../../../constants/ValidationRules';
import { useDocument } from '../../../../../../../../hooks/useDocuments';
import {
  type IFolderOrDocumentItemDTO,
  getListDocumentsByFolder,
} from '../../../../../../../../services/documentsFolder';
import { FolderIcon } from '../Folders/styles';
import { Content, FileIcon, TableCellCustom, TableCustom } from './styles';

interface IDocumentsButtonLinkProps {
  showDisplayOnDashboard?: boolean;
}

export const DocumentsButtonLink = ({
  showDisplayOnDashboard: showDisplayOnDashboardProp,
}: IDocumentsButtonLinkProps = {}): JSX.Element => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    folderId = '',
    clientId = '',
    courseId = '',
    templateId = '',
  } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { breadcrumbs, addBreadcrumb, removeBreadcrumb } = useBreadcrumbs();
  const { handleSelectDocumentLink } = useDocument();

  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [search, setSearch] = useState('');
  const lastSearch = useRef(search);
  const [sortOrder, setSortOrder] = useState<ISort | null>(null);

  const [folder, setFolder] = useState<IFolderDTO | null>();
  const [folderItems, setFolderItems] = useState<IFolderOrDocumentItemDTO[]>(
    []
  );
  const [paginationMeta, setPaginationMeta] =
    useState<IPaginationMetaProps | null>(null);

  const userIsHighRole = useMemo(() => {
    return HighUserRoles.some((role) => user?.roles.includes(role));
  }, [user]);

  const showDisplayOnDashboard =
    (showDisplayOnDashboardProp ?? userIsHighRole) &&
    window.location.origin !== CLIENT_URL;

  const editCourseFolderItemRoutes = getRouteFrom(
    EDIT_COURSE_MODAL_BUTTON_LINK_FOLDER_ITEMS
  );
  const editTemplateFolderRoute = getRouteFrom(
    CREATE_TEMPLATE_MODAL_BUTTON_LINK_FOLDER_ITEMS
  );
  const DocumentHeaderColumns = useMemo(() => {
    const headersArray = [];

    headersArray.push({
      id: 'originalName',
      name: 'File Name',
      textAlign: 'start',
    });
    headersArray.push({
      id: 'name',
      name: 'Description',
      textAlign: 'start',
    });

    if (showDisplayOnDashboard) {
      headersArray.push({
        id: 'highlightedDate',
        name: 'Display on Dashboard',
        textAlign: 'center',
      });
    }

    headersArray.push({
      id: 'createdAt',
      name: 'Upload Date',
      textAlign: 'end',
    });

    return headersArray;
  }, [showDisplayOnDashboard]);

  const getDocuments = useCallback(
    async (
      folderId: string,
      page: number,
      limit: number,
      filter?: string,
      sortOrder?: ISort | null
    ) => {
      setLoadingDocuments(true);
      try {
        const response = await getListDocumentsByFolder({
          folderId: folderId ?? '',
          page,
          limit,
          filter: filter ?? '',
          sort: JSON.stringify(sortOrder ?? {}),
        });

        setFolder(response.folder);
        setPaginationMeta(response.meta);
        setFolderItems((prev) =>
          page === 1 ? response.data : [...prev, ...response.data]
        );
      } catch (err) {
        Logger.debug('err: ', err);
        addToast({
          title: 'Error on load document',
          description: formatUnknownErrorMessage(err),
          styleType: 'error',
          dataCy: 'error-toast',
          duration: 3000,
        });
      } finally {
        setLoadingDocuments(false);
      }
    },
    [addToast]
  );

  useEffect(() => {
    void getDocuments(folderId, INIT_PAGE, LIMIT_PAGE, search, sortOrder);
  }, [getDocuments, folderId, sortOrder]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (search.trim() !== lastSearch.current) {
      const execSearch = () => {
        lastSearch.current = search.trim();
        setFolderItems([]);
        searchDocument(true);
      };

      if (search.trim() === '') {
        execSearch();
      } else {
        timer = setTimeout(execSearch, DEBOUNCE_TIME);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [search, sortOrder]);

  const searchDocument = (searchByButton = false) => {
    if (!loadingDocuments || searchByButton) {
      const pageAux = searchByButton
        ? 0
        : Number((paginationMeta?.page ?? 0) > 0 ? paginationMeta?.page : 0);
      void getDocuments(folderId, pageAux + 1, LIMIT_PAGE, search, sortOrder);
    }
  };

  const removeBreadcrumbPage = useCallback(
    (index: number) => {
      removeBreadcrumb(index);
    },
    [removeBreadcrumb]
  );

  const toRouteSubFolderEditCourse = useCallback(
    (folderId: number | string) =>
      generatePath(editCourseFolderItemRoutes, {
        folderId: folderId.toString(),
        clientId,
        courseId,
      }),
    [clientId, courseId, editCourseFolderItemRoutes]
  );

  const toRouteSubFolderEditTemplate = useCallback(
    (folderId: number | string) =>
      generatePath(editTemplateFolderRoute, {
        folderId: folderId.toString(),
        clientId,
        templateId,
      }),
    [clientId, templateId, editTemplateFolderRoute]
  );

  const handleFolderClick = useCallback(
    (item: IFolderDTO) => {
      let route = '';
      if (courseId) {
        route = toRouteSubFolderEditCourse(item.id);
      } else {
        route = toRouteSubFolderEditTemplate(item.id);
      }
      addBreadcrumb({
        name: item.name,
        url: route,
      });
      navigate(route, { state: { from: pathname } });
    },
    [
      addBreadcrumb,
      courseId,
      navigate,
      pathname,
      toRouteSubFolderEditCourse,
      toRouteSubFolderEditTemplate,
    ]
  );

  const handleSelectDocument = useCallback(
    (item: IDocumentDTO) => {
      handleSelectDocumentLink(item);
    },
    [handleSelectDocumentLink]
  );

  const formatDocumentName = (document: IDocumentDTO) => {
    const documentExtension = document.extension?.trim();
    if (document.originalName) {
      if (
        !documentExtension ||
        document.originalName.endsWith(`.${documentExtension}`)
      ) {
        return document.originalName;
      }

      return `${document.originalName}.${document.extension.replace(/^\./, '')}`;
    }

    if (!document.name?.trim()) {
      return '-';
    }

    if (!documentExtension || document.name.endsWith(`.${documentExtension}`)) {
      return document.name;
    }

    return `${document.name}.${document.extension.replace(/^\./, '')}`;
  };

  const formDate = (date: string) => {
    const dateAux = format(new Date(date), 'MM-dd-yyyy');
    return dateAux;
  };

  return (
    <Content>
      <SearchBar
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setSearch(e.target.value);
        }}
        onClearInput={() => {
          setSearch('');
        }}
        search={search}
        maxLength={VALIDATION_LIMITS.PAGE.SEARCH_MAX}
        loading={loadingDocuments}
        id="search-document"
        dataCy="input-searchDocument"
      />
      <BreadcrumbsComponent
        links={breadcrumbs}
        onClick={removeBreadcrumbPage}
      />
      {loadingDocuments && !folderItems.length ? (
        <ListLoading />
      ) : (
        <TableCustom
          columns={DocumentHeaderColumns}
          dataLength={folderItems.length}
          next={() => {
            searchDocument();
          }}
          loading={loadingDocuments}
          hasMore={
            paginationMeta?.total !== undefined &&
            folderItems.length < paginationMeta.total
          }
          noItensMessage="No items found"
          onClickHeader={(column: IColumn) => {
            setSortOrder({
              id: column.id,
              direction: sortOrder?.direction === 'ASC' ? 'DESC' : 'ASC',
            });
          }}
          columnSorted={sortOrder}
        >
          {folderItems.map((item) =>
            item.type === 'folder' ? (
              <TableRow
                key={item.id}
                onClick={() => {
                  handleFolderClick(item);
                }}
              >
                <TableImageCell
                  text={item.name}
                  subtitle=""
                  dataCy="folder-card"
                  type="icon"
                >
                  <FolderIcon />
                </TableImageCell>
                <TableCellCustom text="" />
                {showDisplayOnDashboard && <TableCellCustom text="" />}
                <TableCellCustom
                  textAlign="end"
                  text={formDate(item.updatedAt ?? item.createdAt)}
                />
              </TableRow>
            ) : (
              <TableRow
                key={`document-${item.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectDocument(item);
                }}
              >
                {folder?.typeFolder === ETypeFolder.RENEWAL && (
                  <>
                    <TableImageCell
                      text={
                        item.name
                          ? `${item.name}`
                          : (item.originalName ?? 'unknown')
                      }
                      subtitle=""
                      dataCy={`document-card-${item.id}`}
                      type="icon"
                    >
                      <FileIcon />
                    </TableImageCell>

                    <TableCell text={item.documentType} />
                    <TableCell text={String(item.createdBy?.fullName ?? '')} />
                    <TableCell text={item.approvedDeclinedBy?.fullName ?? ''} />
                  </>
                )}

                {folder?.typeFolder !== ETypeFolder.RENEWAL && (
                  <>
                    <TableImageCell
                      text={formatDocumentName(item)}
                      subtitle=""
                      dataCy={`documents-card-${item.id}`}
                      type="icon"
                    >
                      <FileIcon />
                    </TableImageCell>
                    <TableCellCustom
                      textAlign="start"
                      text={item.name ? item.name : '-'}
                    />
                    {showDisplayOnDashboard && (
                      <TableCellCustom
                        text={item.highlightedDate != null ? 'Yes' : 'No'}
                      />
                    )}
                  </>
                )}

                <TableCellCustom
                  text={formDate(item.createdAt)}
                  textAlign="end"
                />
              </TableRow>
            )
          )}
        </TableCustom>
      )}
    </Content>
  );
};
